import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Profile from "./Profile";
import "./Navbar.css";
import {BASE_URL} from "./Constants";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const navigate = useNavigate();
  const toggleMenu = () => setMenuOpen(!menuOpen);

  // ---------------- Fetch logged-in user ----------------
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/get_user_details`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) throw new Error("User not logged in");
        const data = await res.json();
        setUser(data);
      } catch (err) {
        console.log("User not logged in:", err.message);
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  // ---------------- Fetch cart count ----------------
  const fetchCartCount = useCallback(async (event) => {
      try {
        // :white_check_mark: use user_id from event OR from user state
        const userId = event?.detail?.user_id || user?.user_id;
        if (!userId) {
          setCartCount(0);
          return;
        }

        const res = await fetch(
          `${BASE_URL}/cart/fetch_cart_details?user_id=${userId}`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error("Failed to fetch cart");
        const data = await res.json();

        const totalQuantity = data.items
          ? data.items.reduce((total, item) => total + item.quantity, 0)
          : 0;

        setCartCount(totalQuantity);
      } catch (err) {
        console.error("Error fetching cart count:", err);
        setCartCount(0);
      }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchCartCount();
    }
    window.addEventListener("cartUpdated", fetchCartCount);
    return () => window.removeEventListener("cartUpdated", fetchCartCount);
  }, [fetchCartCount, user]);

  
  // ---------------- Handle search input ----------------
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchQuery.trim().length > 1) {
        fetchSearchResults(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);


  const fetchSearchResults = async (query) => {
    try {
      const res = await fetch(`${BASE_URL}/search_items?query=${encodeURIComponent(query)}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error("Search failed");
      const data = await res.json();
      const results = Array.isArray(data) ? data : data.message;
      setSearchResults(results || []);
      setShowDropdown(true);
    } catch (err) {
      console.error("Error fetching search results:", err);
      setSearchResults([]);
    }
  };


  const handleResultClick = (itemId) => {
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
    navigate(`/product/${itemId}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim() !== "") {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setShowDropdown(false);
    }
  };

  return (
    <nav className="nav-bar">
      <div className="navbar-left">
        <img src="/assets/logo_1.png" alt="Company Logo" className="company-logo" />
        <div className="hamburger" onClick={toggleMenu}>☰</div>
        <ul className={`nav-links ${menuOpen ? "active" : ""}`}>
          <li><Link to="/" onClick={toggleMenu}>Home</Link></li>
          <li><Link to="/shop" onClick={toggleMenu}>Shop</Link></li>
        </ul>
      </div>

      <div className="navbar-right">
        <form onSubmit={handleSearchSubmit} className="search-container">
          <input
            type="text"
            value={searchQuery}
            placeholder="Search for product and brands"
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchResults.length && setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            className="search-input"
          />
          {showDropdown && searchResults.length > 0 && (
            <ul className="search-dropdown">
              {searchResults.map((item) => (
                <li key={item.item_id} onClick={() => handleResultClick(item.item_id)}>
                  <div className="dropdown-text">
                    <span className="dropdown-name">{item.name}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </form>

        <Profile />
        <Link to="/wishlist" className="nav-icons">
          <img src="/assets/wishlist.png" alt="wishlist" className="icon-image" />
          <span>Wishlist</span>
        </Link>
        <Link to="/cart" className="nav-icons">
          <img src="/assets/cart.png" alt="cart" className="icon-image" />
          <span>Cart</span>
          {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
        </Link>
      </div>
    </nav>
  );
};

export default Navbar;
